/**
 * Main App Component
 * Sets up routing and providers
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Outlet, NavLink, Navigate, useMatch, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RiskDetail } from './pages/RiskDetail';
import { RiskList } from './pages/RiskList';
import { IncidentDetail } from './pages/IncidentDetail';
import { IncidentList } from './pages/IncidentList';
import { IssueDetail } from './pages/IssueDetail';
import { IssueList } from './pages/IssueList';
import { RemediationDetail } from './pages/RemediationDetail';
import { RemediationList } from './pages/RemediationList';
import { ThreatDetail } from './pages/ThreatDetail';
import { ThreatList } from './pages/ThreatList';
import { EvidenceRecordDetail } from './pages/EvidenceRecordDetail';
import { EvidenceList } from './pages/EvidenceList';
import { ControlDetail } from './pages/ControlDetail';
import { ControlsList } from './pages/ControlsList';
import { ControlMatricesList } from './pages/ControlMatricesList';
import { DocumentsList } from './pages/DocumentsList';
import { GovernanceInboxPage } from './pages/GovernanceInboxPage';
import { HubLayout } from './pages/hub/HubLayout';
import { HubCategoryOverview } from './pages/hub/HubCategoryOverview';
import { HubDocumentPage } from './pages/hub/HubDocumentPage';
import { HubInitiativeTracker } from './pages/hub/HubInitiativeTracker';
import { HubServiceCatalog } from './pages/hub/HubServiceCatalog';
import { HubSystemDescriptionPage } from './pages/hub/HubSystemDescriptionPage';
import { HubAiUseCaseWizard } from './pages/hub/HubAiUseCaseWizard';
import { DocumentDetail } from './pages/DocumentDetail';
import { RegistersPage } from './pages/RegistersPage';
import { IncidentRegistersPage } from './pages/IncidentRegistersPage';
import { IssueRegistersPage } from './pages/IssueRegistersPage';
import { RemediationRegistersPage } from './pages/RemediationRegistersPage';
import { ThreatRegistersPage } from './pages/ThreatRegistersPage';
import { EvidenceRegistersPage } from './pages/EvidenceRegistersPage';
import { DocumentRegistersPage } from './pages/DocumentRegistersPage';
import { DepartmentsPage } from './pages/DepartmentsPage';
import { PhysicalSitesPage } from './pages/PhysicalSitesPage';
import { PhysicalSiteFormPage } from './pages/PhysicalSiteFormPage';
import { LegalRegisterPage } from './pages/LegalRegisterPage';
import { LegalRequirementFormPage } from './pages/LegalRequirementFormPage';
import { InterestedPartiesPage } from './pages/InterestedPartiesPage';
import { InterestedPartyFormPage } from './pages/InterestedPartyFormPage';
import { AssetDetailPage } from './pages/AssetDetailPage';
import { SecurityObjectivesPage } from './pages/SecurityObjectivesPage';
import { SecurityObjectiveFormPage } from './pages/SecurityObjectiveFormPage';
import { PeoplePage } from './pages/PeoplePage';
import { AssetsPage } from './pages/AssetsPage';
import { UsersPage } from './pages/UsersPage';
import { RaciAdminPage } from './pages/RaciAdminPage';
import { BulkRaciPage } from './pages/BulkRaciPage';
import { SettingsPage } from './pages/SettingsPage';
import { AdminFrameworksPage } from './pages/AdminFrameworksPage';
import { FrameworkDetail } from './pages/FrameworkDetail';
import { AdminMappingsPage } from './pages/AdminMappingsPage';
import { AdminPortalPage } from './pages/AdminPortalPage';
import { AiUsagePage } from './pages/AiUsagePage';
import { DashboardPage } from './pages/DashboardPage';
import { ReportsPage } from './pages/ReportsPage';

import { IsmsJourneyPage } from './pages/IsmsJourneyPage';
import { IsmsTaskGuidePage } from './pages/IsmsTaskGuidePage';
import { ReadinessPage } from './pages/ReadinessPage';
import { ReadinessWizard } from './pages/ReadinessWizard';
import { ReadinessResults } from './pages/ReadinessResults';
import { AiDashboard } from './pages/ai/AiDashboard';
import { AiUseCaseList } from './pages/ai/AiUseCaseList';
import { AiUseCaseDetail } from './pages/ai/AiUseCaseDetail';
import { AiUseCaseReport } from './pages/ai/AiUseCaseReport';
import { MeetingMinutesList } from './pages/minutes/MeetingMinutesList';
import { MeetingMinutesDetail } from './pages/minutes/MeetingMinutesDetail';
import { ProcessingActivityList } from './pages/privacy/ProcessingActivityList';
import { ProcessingActivityDetail } from './pages/privacy/ProcessingActivityDetail';
import { ProcessingActivityReport } from './pages/privacy/ProcessingActivityReport';
import { SubprocessorsList } from './pages/privacy/SubprocessorsList';
import { SubprocessorDetail } from './pages/privacy/SubprocessorDetail';
import { DataInventoryPage } from './pages/privacy/DataInventoryPage';
import { AiUseCaseWizard } from './pages/ai/wizard/AiUseCaseWizard';
import { PestleListPage } from './pages/PestleListPage';
import { ISMSScopePage } from './pages/ISMSScopePage';
import { PestleWizard } from './pages/PestleWizard';
import { StatementOfApplicabilityPage } from './pages/StatementOfApplicabilityPage';
import { ControlConfigPage } from './pages/ControlConfigPage';
import { RiskConfigPage } from './pages/RiskConfigPage';
import { IncidentConfigPage } from './pages/IncidentConfigPage';
import { IssueConfigPage } from './pages/IssueConfigPage';
import { RemediationConfigPage } from './pages/RemediationConfigPage';
import { ThreatConfigPage } from './pages/ThreatConfigPage';
import { EvidenceConfigPage } from './pages/EvidenceConfigPage';
import { PolicyTemplatesPage } from './pages/PolicyTemplatesPage';
import { FieldCatalogBuilder } from './pages/FieldCatalogBuilder';
import { TemplateBuilderPage } from './pages/TemplateBuilderPage';
import { IsmsJourneyAdminPage } from './pages/IsmsJourneyAdminPage';
import { MaintenanceTasks } from './pages/MaintenanceTasks';
import { ManagementReviewList } from './pages/ManagementReviewList';
import { ManagementReviewDetail } from './pages/ManagementReviewDetail';
import { useAuth } from './hooks/useAuth';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import { isPlatformAdmin, isSuperAdmin } from './utils/adminAccess';
import { SuperAdminPage } from './pages/SuperAdminPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/auth/LoginPage';
import { AuthCallbackPage } from './pages/auth/AuthCallbackPage';
import { SetPasswordPage } from './pages/auth/SetPasswordPage';
import { AcceptInvitationPage } from './pages/auth/AcceptInvitationPage';
import { NoAccessPage } from './pages/auth/NoAccessPage';

// Initiative Tracker (merged from the standalone tracker app, now behind the same login)
import { OverviewPage as TrackerOverviewPage } from './pages/OverviewPage';
import { MyActionsPage } from './pages/MyActionsPage';
import { AllInitiativesPage } from './pages/AllInitiativesPage';
import { InitiativeDetailPage } from './pages/InitiativeDetailPage';
import { InitiativeFormPage } from './pages/InitiativeFormPage';
import { SubmitReportPage } from './pages/SubmitReportPage';
import { CatalogPage as TrackerCatalogPage } from './pages/CatalogPage';
import { ServiceDetailPage } from './pages/ServiceDetailPage';
import { MetricDetailPage } from './pages/MetricDetailPage';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // Don't auto-refetch just because the user alt-tabbed and came back.
      // Background API storms on window focus are the #1 cause of spurious
      // 401s that wipe the session while the user is mid-form.
      refetchOnWindowFocus: false,
      // 5-minute stale time: data is fresh enough within a page visit.
      // Individual queries that need real-time data can override this locally.
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Helper: nav item with active class on <li> (Materio requires active on the li)
const MenuItem: React.FC<{ to: string; icon: string; label: string }> = ({ to, icon, label }) => {
  // Strip query string before matching so e.g. /reports?tab=gap still highlights correctly
  const pathOnly = to.split('?')[0];
  const match = useMatch({ path: pathOnly, end: pathOnly === '/dashboard' });
  return (
    <li className={`menu-item${match ? ' active' : ''}`}>
      <NavLink to={to} className="menu-link">
        <i className={`menu-icon tf-icons ${icon}`}></i>
        <div>{label}</div>
      </NavLink>
    </li>
  );
};

/** "Documentation" is one nav entry standing in for two still-separate content-library
 * categories (Policies, Processes - distinct registers, e.g. because System Description's
 * Low-context sourcing treats them as two distinct pools). Points at /hub/policies as its
 * landing page, but MenuItem's single-path match would only ever highlight it there, not
 * while actually browsing Processes - so this checks both prefixes directly instead of
 * reusing MenuItem. */
const HubDocumentationMenuItem: React.FC = () => {
  const location = useLocation();
  const active = matchesPrefix(location.pathname, ['/hub/policies', '/hub/processes']);
  return (
    <li className={`menu-item${active ? ' active' : ''}`}>
      <NavLink to="/hub/policies" className="menu-link">
        <i className="menu-icon tf-icons ri-book-2-line"></i>
        <div>Documentation</div>
      </NavLink>
    </li>
  );
};

// Helper: collapsible nav group with child items (Materio accordion pattern)
type MenuGroupChild = { to: string; icon: string; label: string };
const MenuGroup: React.FC<{ icon: string; label: string; children: MenuGroupChild[] }> = ({
  icon,
  label,
  children,
}) => {
  const location = useLocation();
  const isAnyChildActive = children.some((c) => location.pathname === c.to || location.pathname.startsWith(`${c.to}/`));
  const [open, setOpen] = React.useState(isAnyChildActive);

  // Keep open when navigating to a child route
  React.useEffect(() => {
    if (isAnyChildActive) setOpen(true);
  }, [isAnyChildActive]);

  return (
    <li className={`menu-item${open ? ' open' : ''}${isAnyChildActive ? ' active' : ''}`}>
      <a
        href="javascript:void(0);"
        className="menu-link menu-toggle"
        onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }}
      >
        <i className={`menu-icon tf-icons ${icon}`}></i>
        <div>{label}</div>
      </a>
      <ul className="menu-sub">
        {children.map((child) => {
          const active = location.pathname === child.to || location.pathname.startsWith(`${child.to}/`);
          return (
            <li key={child.to} className={`menu-item${active ? ' active' : ''}`}>
              <NavLink to={child.to} className="menu-link">
                <i className={`menu-icon tf-icons ${child.icon}`}></i>
                <div>{child.label}</div>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </li>
  );
};

// ─── Security / Privacy space switcher ─────────────────────────────────────
//
// Two products sharing one login and one sidebar shell. The split is presentation-only —
// no page is duplicated or rebuilt, only which menu items render and which of the two
// spaces the shell currently presents itself as.
//
// Persisted the same way every other piece of cross-page state in this app is (see
// useAuth.ts, adminAccess.ts): a plain localStorage key read directly, no Context/store.
type NimbusSpace = 'security' | 'privacy' | 'admin' | 'hub' | 'project';
const SPACE_STORAGE_KEY = 'nimbusSpace';
const SPACE_LABELS: Record<NimbusSpace, string> = { security: 'Security', privacy: 'Privacy', admin: 'Admin', hub: 'Hub', project: 'Project' };

// Deliberately defaults to light rather than following prefers-color-scheme: every custom
// --np-* token this app's own component library adds resolves through the vendor theme's own
// [data-bs-theme=dark] overrides (confirmed - they're all var(--bs-*) aliases, never a
// hardcoded hex), so dark mode should already render correctly, but it has never actually been
// exercised anywhere in this app before now. Defaulting to the (extensively used, known-good)
// light theme means a user whose OS happens to prefer dark never sees this untested rendering
// unless they deliberately opt in with the toggle below.
type NimbusTheme = 'light' | 'dark';
const THEME_STORAGE_KEY = 'nimbusTheme';

/** Pages that exist only in the Privacy space. Landing on one of these — via a link, a
 *  bookmark, or the back button, not just the switcher — flips the whole shell into
 *  Privacy, the same way a browser tab's chrome follows the site you're actually on. */
const PRIVACY_ONLY_PATH_PREFIXES = ['/legal-register', '/processing-activities', '/subprocessors', '/ai-use-cases', '/ai-governance', '/data-inventory', '/governance-inbox'];

/** Pages that exist only in the Admin space — platform/tenant administration tools an
 *  average user never needs, and (per the menu body below) never even sees a link to. */
const ADMIN_ONLY_PATH_PREFIXES = ['/users', '/admin', '/super-admin', '/ai-usage'];

/** Pages in the Hub space — the internal intranet area (documentation, policies, training,
 *  process guides). Unlike Admin, nothing here is role-gated: every authenticated tenant user
 *  can see it, same as Security/Privacy. */
const HUB_ONLY_PATH_PREFIXES = ['/hub'];

/** Pages in the Project space — the Initiative Tracker (incl. Service Catalog) and Meeting
 *  Minutes. Previously shared/duplicated across Security and Privacy; neither is actually
 *  GRC-domain-specific, so they get their own space instead, the same reasoning that put
 *  Hub and Admin here. Hub keeps its own separate, read-only /hub/tracker and
 *  /hub/service-catalog views — those stay HUB_ONLY, untouched by this. */
const PROJECT_ONLY_PATH_PREFIXES = ['/tracker', '/meeting-minutes'];

/** Pages that belong to no space specifically (Dashboard, Organisation, Settings…). Visiting
 *  one leaves whatever space the user is already in untouched, rather than forcing a flip
 *  either way. */
const SHARED_PATH_PREFIXES = [
  '/dashboard', '/people', '/departments', '/physical-sites', '/assets', '/settings',
];

const matchesPrefix = (pathname: string, prefixes: string[]): boolean =>
  prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Layout component
const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const userEmail = localStorage.getItem('userEmail') || '';
  // Two different "admin" concepts, kept distinct on purpose — see adminAccess.ts's own
  // header comment. isAdmin (platform operator, Mercell staff) already gated Field
  // Catalog/Template Builder/Journey Editor before the Admin space existed; isTenantAdmin
  // (a customer's own admin, the `role` on their account) is what the Admin space itself —
  // and Team Management/Admin Portal specifically — is gated on.
  const isAdmin = isPlatformAdmin(userEmail);
  const isTenantAdmin = (user?.role || '').toLowerCase() === 'admin';
  const isSuperAdminUser = isSuperAdmin();
  const canAccessAdminSpace = isTenantAdmin || isAdmin || isSuperAdminUser;
  const userInitial = (user?.name || user?.email || 'U')[0].toUpperCase();
  useInactivityLogout();

  const location = useLocation();
  const navigate = useNavigate();

  const [space, setSpaceState] = React.useState<NimbusSpace>(
    () => (localStorage.getItem(SPACE_STORAGE_KEY) as NimbusSpace | null) || 'security',
  );
  const setSpace = React.useCallback((next: NimbusSpace) => {
    localStorage.setItem(SPACE_STORAGE_KEY, next);
    setSpaceState(next);
  }, []);
  const [spaceMenuOpen, setSpaceMenuOpen] = React.useState(false);

  const [theme, setTheme] = React.useState<NimbusTheme>(
    () => (localStorage.getItem(THEME_STORAGE_KEY) as NimbusTheme | null) || 'light',
  );
  React.useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);
  const toggleTheme = React.useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  // Auto-follow the current route, so the sidebar can never disagree with the page the
  // user is actually looking at (which would happen the moment someone follows a link,
  // opens a bookmark, or hits back into the other space without touching the switcher).
  //
  // A manual switch (below) changes `space` and `location.pathname` via two separate
  // calls - setSpace, then navigate - that do not reliably land in the same commit.
  // There is a real intermediate render where `space` already reflects the space just
  // picked but `location.pathname` still reflects the page being left. Without the ref
  // guard below, that intermediate render makes this same effect "correct" space right
  // back to whatever the stale, about-to-change path implies - a same-value no-op most
  // of the time (the old path still matches the old space), but a genuine, permanently
  // stuck loop whenever the space being left owns the current path itself (confirmed
  // live for Hub and Project, whose own pages are never in SHARED_PATH_PREFIXES):
  // switching away from /tracker or /hub/tracker got silently reverted before the
  // navigate to /dashboard was ever seen, landing right back where you started.
  const manualSwitchRef = React.useRef(false);

  React.useEffect(() => {
    if (manualSwitchRef.current) {
      manualSwitchRef.current = false;
      return;
    }
    const path = location.pathname;
    if (matchesPrefix(path, PRIVACY_ONLY_PATH_PREFIXES)) {
      setSpace('privacy');
    } else if (matchesPrefix(path, ADMIN_ONLY_PATH_PREFIXES)) {
      setSpace('admin');
    } else if (matchesPrefix(path, HUB_ONLY_PATH_PREFIXES)) {
      setSpace('hub');
    } else if (matchesPrefix(path, PROJECT_ONLY_PATH_PREFIXES)) {
      setSpace('project');
    } else if (!matchesPrefix(path, SHARED_PATH_PREFIXES)) {
      setSpace('security');
    }
  }, [location.pathname, space, setSpace]);

  const switchSpace = (next: NimbusSpace) => {
    manualSwitchRef.current = true;
    setSpace(next);
    setSpaceMenuOpen(false);
    navigate('/dashboard');
  };

  const meetingMinutesItem = <MenuItem to="/meeting-minutes" icon="ri-file-list-3-line" label="Meeting Minutes" />;
  const initiativeTrackerMenuGroup = (
    <MenuGroup icon="ri-flashlight-line" label="Initiative Tracker" children={[
      { to: '/tracker', icon: 'ri-dashboard-line', label: 'Initiative Overview' },
      { to: '/tracker/my-actions', icon: 'ri-user-line', label: 'My Actions' },
      { to: '/tracker/initiatives', icon: 'ri-list-check-2', label: 'All Initiatives' },
      { to: '/tracker/initiatives/new', icon: 'ri-add-circle-line', label: 'New Initiative' },
      { to: '/tracker/catalog', icon: 'ri-archive-drawer-line', label: 'Service Catalog' },
    ]} />
  );

  const openMenu = React.useCallback(() => {
    if (window.innerWidth < 1200) {
      document.documentElement.classList.add('layout-menu-expanded');
    } else {
      document.documentElement.classList.remove('layout-menu-collapsed');
    }
  }, []);

  const closeMenu = React.useCallback(() => {
    document.documentElement.classList.remove('layout-menu-expanded');
  }, []);

  // Close menu on route change (mobile nav)
  React.useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  // Native event listener on the X button to bypass iOS position:fixed + will-change:transform
  // compositing hit-test bug (click events misfire; touchend is immune to this)
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    const el = closeButtonRef.current;
    if (!el) return;
    const handler = (e: Event) => {
      e.preventDefault();
      if (window.innerWidth < 1200) {
        // Mobile: slide sidebar off screen
        document.documentElement.classList.remove('layout-menu-expanded');
      } else {
        // Desktop: collapse sidebar to icon-only rail
        document.documentElement.classList.add('layout-menu-collapsed');
      }
    };
    el.addEventListener('touchend', handler, { passive: false });
    el.addEventListener('click', handler);
    return () => {
      el.removeEventListener('touchend', handler);
      el.removeEventListener('click', handler);
    };
  }, []);

  // Hover-to-expand: when the sidebar is collapsed on desktop, hovering it
  // temporarily adds layout-menu-hover to <html>, which the theme CSS uses to
  // re-show the full-width sidebar while the cursor is inside it.
  const sidebarRef = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    const onEnter = () => {
      if (window.innerWidth >= 1200 && document.documentElement.classList.contains('layout-menu-collapsed')) {
        document.documentElement.classList.add('layout-menu-hover');
      }
    };
    const onLeave = () => {
      document.documentElement.classList.remove('layout-menu-hover');
    };
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">

        {/* Sidebar */}
        <aside ref={sidebarRef} id="layout-menu" className="layout-menu menu-vertical menu bg-menu-theme">
          <div className="app-brand demo">
            <div className="position-relative flex-grow-1">
              <button
                type="button"
                className="app-brand-link border-0 bg-transparent p-0 d-flex align-items-center w-100"
                style={{ cursor: 'pointer' }}
                onClick={() => setSpaceMenuOpen((o) => !o)}
                aria-expanded={spaceMenuOpen}
              >
                <span className="app-brand-logo demo">
                  <i className="ri-cloud-line ri-2x text-primary"></i>
                </span>
                <span className="app-brand-text demo menu-text fw-semibold ms-2">
                  Mercell Nimbus: {SPACE_LABELS[space]}
                </span>
                <i className="ri-arrow-down-s-line menu-text ms-1" style={{ fontSize: '0.9rem' }}></i>
              </button>
              {spaceMenuOpen && (
                <>
                  {/* Full-screen click-catcher to close on outside click, matching the
                      np-popover pattern used everywhere else in the app (RegisterItemList.tsx
                      etc.) rather than depending on Bootstrap's dropdown JS. */}
                  <div style={{ position: 'fixed', inset: 0, zIndex: 1040 }} onClick={() => setSpaceMenuOpen(false)} />
                  <div className="np-popover" style={{ top: '100%', left: 0, marginTop: '0.5rem', minWidth: '15rem' }}>
                    <div className="np-popover-head"><span>Switch area</span></div>
                    <div
                      className={`np-popover-item${space === 'security' ? ' active' : ''}`}
                      onClick={() => switchSpace('security')}
                    >
                      <i className="ri-shield-check-line"></i>
                      <div>
                        <div className="fw-semibold">Mercell Nimbus: Security</div>
                        <div className="np-sub" style={{ fontSize: '0.7rem' }}>ISO 27001 &amp; ISMS</div>
                      </div>
                    </div>
                    <div
                      className={`np-popover-item${space === 'privacy' ? ' active' : ''}`}
                      onClick={() => switchSpace('privacy')}
                    >
                      <i className="ri-shield-user-line"></i>
                      <div>
                        <div className="fw-semibold">Mercell Nimbus: Privacy</div>
                        <div className="np-sub" style={{ fontSize: '0.7rem' }}>GDPR &amp; AI Governance</div>
                      </div>
                    </div>
                    <div
                      className={`np-popover-item${space === 'project' ? ' active' : ''}`}
                      onClick={() => switchSpace('project')}
                    >
                      <i className="ri-flashlight-line"></i>
                      <div>
                        <div className="fw-semibold">Mercell Nimbus: Project</div>
                        <div className="np-sub" style={{ fontSize: '0.7rem' }}>Initiatives &amp; Meeting Minutes</div>
                      </div>
                    </div>
                    <div
                      className={`np-popover-item${space === 'hub' ? ' active' : ''}`}
                      onClick={() => switchSpace('hub')}
                    >
                      <i className="ri-compass-3-line"></i>
                      <div>
                        <div className="fw-semibold">Mercell Nimbus: Hub</div>
                        <div className="np-sub" style={{ fontSize: '0.7rem' }}>Documentation &amp; Policies</div>
                      </div>
                    </div>
                    {canAccessAdminSpace && (
                      <div
                        className={`np-popover-item${space === 'admin' ? ' active' : ''}`}
                        onClick={() => switchSpace('admin')}
                      >
                        <i className="ri-admin-line"></i>
                        <div>
                          <div className="fw-semibold">Mercell Nimbus: Admin</div>
                          <div className="np-sub" style={{ fontSize: '0.7rem' }}>Platform Administration</div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <button ref={closeButtonRef} type="button" className="layout-menu-toggle menu-link text-large ms-auto border-0 bg-transparent p-2 d-flex align-items-center justify-content-center" style={{ minWidth: '2.75rem', minHeight: '2.75rem', cursor: 'pointer', touchAction: 'manipulation' }}>
              <i className="ri-close-fill align-middle"></i>
            </button>
          </div>

          <div className="menu-inner-shadow"></div>

          <ul className="menu-inner py-1">
            <MenuItem to="/dashboard" icon="ri-home-5-line" label="Dashboard" />

            {space === 'security' && (
              <>
                <li className="menu-header small text-uppercase">
                  <span className="menu-header-text">Get Started</span>
                </li>
                <MenuItem to="/readiness" icon="ri-award-line" label="ISO 27001 Readiness" />
                <MenuItem to="/isms-journey" icon="ri-map-2-line" label="ISMS Journey" />
                <MenuItem to="/reports?tab=gap" icon="ri-git-branch-line" label="Gap Analysis" />
              </>
            )}

            {/* Security only — Organisation data (people, departments, sites, assets) has no
                bearing on Privacy's data-protection registers, Hub's reference content, or
                Admin's platform-administration tools, so it's hidden in all three. */}
            {space === 'security' && (
              <>
                <li className="menu-header small text-uppercase">
                  <span className="menu-header-text">Organisation</span>
                </li>
                <MenuItem to="/people" icon="ri-user-line" label="People" />
                <MenuItem to="/departments" icon="ri-building-line" label="Departments" />
                <MenuItem to="/physical-sites" icon="ri-map-pin-2-line" label="Physical Sites" />
                <MenuItem to="/assets" icon="ri-database-line" label="Assets" />
              </>
            )}

            {space === 'security' && (
              <>
                <li className="menu-header small text-uppercase">
                  <span className="menu-header-text">Design</span>
                </li>
                <MenuItem to="/pestle" icon="ri-mind-map" label="Context (PESTLE)" />
                <MenuItem to="/interested-parties" icon="ri-group-line" label="Interested Parties" />
                <MenuItem to="/security-objectives" icon="ri-shield-star-line" label="Security Objectives" />
                <MenuItem to="/statement-of-applicability" icon="ri-file-list-3-line" label="Statement of Applicability" />
                <MenuItem to="/isms-scope" icon="ri-focus-3-line" label="ISMS Scope" />
                <MenuItem to="/raci" icon="ri-team-line" label="RACI" />
                <MenuGroup icon="ri-file-text-line" label="Documents" children={[
                  { to: '/document-registers', icon: 'ri-folder-open-line', label: 'Document Registers' },
                  { to: '/policy-templates', icon: 'ri-draft-line', label: 'Policy Templates' },
                ]} />

                <li className="menu-header small text-uppercase">
                  <span className="menu-header-text">Operate</span>
                </li>
                <MenuItem to="/registers" icon="ri-folders-line" label="Risk Registers" />
                <MenuItem to="/control-matrices" icon="ri-shield-check-line" label="Control Matrices" />
                <MenuItem to="/evidence-registers" icon="ri-file-list-3-line" label="Evidence Registers" />
                <MenuItem to="/issue-registers" icon="ri-folders-line" label="Issue Registers" />
                <MenuItem to="/incident-registers" icon="ri-alert-line" label="Incident Registers" />
                <MenuItem to="/threat-registers" icon="ri-spy-line" label="Threat Registers" />
                <MenuItem to="/remediation-registers" icon="ri-tools-line" label="Remediation Registers" />

                <li className="menu-header small text-uppercase">
                  <span className="menu-header-text">Improve</span>
                </li>
                <MenuItem to="/reports" icon="ri-bar-chart-box-line" label="Reports" />

                <li className="menu-header small text-uppercase">
                  <span className="menu-header-text">Maintain</span>
                </li>
                <MenuItem to="/maintenance" icon="ri-calendar-check-line" label="Maintenance Tasks" />
                <MenuItem to="/management-reviews" icon="ri-clipboard-line" label="Management Reviews" />
              </>
            )}

            {space === 'privacy' && (
              <>
                <li className="menu-header small text-uppercase">
                  <span className="menu-header-text">Data Protection</span>
                </li>
                <MenuItem to="/governance-inbox" icon="ri-inbox-line" label="Governance Inbox" />
                <MenuItem to="/legal-register" icon="ri-scales-3-line" label="Legal Register" />
                <MenuItem to="/processing-activities" icon="ri-database-2-line" label="Processing Activities" />
                <MenuItem to="/subprocessors" icon="ri-shield-keyhole-line" label="Controllers & Processors" />
                <MenuItem to="/ai-use-cases" icon="ri-robot-line" label="AI Use Case Register" />
                <MenuItem to="/ai-governance" icon="ri-robot-2-line" label="AI Governance Dashboard" />
                <MenuItem to="/data-inventory" icon="ri-database-2-line" label="Data Inventory" />
              </>
            )}

            {space === 'project' && (
              <>
                <li className="menu-header small text-uppercase">
                  <span className="menu-header-text">Project</span>
                </li>
                {initiativeTrackerMenuGroup}
                {meetingMinutesItem}
              </>
            )}

            {space === 'hub' && (
              <>
                <li className="menu-header small text-uppercase">
                  <span className="menu-header-text">Hub</span>
                </li>
                <HubDocumentationMenuItem />
                <MenuItem to="/hub/customer-assurance" icon="ri-customer-service-2-line" label="Customer Assurance" />
                <MenuItem to="/hub/training" icon="ri-graduation-cap-line" label="Training & Awareness" />
                <MenuItem to="/hub/dpias" icon="ri-shield-user-line" label="DPIAs" />
                <MenuItem to="/hub/tracker" icon="ri-flashlight-line" label="Initiative Tracker" />
                <MenuItem to="/hub/service-catalog" icon="ri-archive-drawer-line" label="Service Catalog" />
                <MenuItem to="/hub/system-description" icon="ri-file-text-line" label="System Description" />
                <MenuItem to="/hub/ai-use-case-wizard" icon="ri-robot-line" label="Log an AI Use Case" />
              </>
            )}

            {space === 'admin' && (
              <>
                <li className="menu-header small text-uppercase">
                  <span className="menu-header-text">Platform Administration</span>
                </li>
                {isTenantAdmin && <MenuItem to="/users" icon="ri-team-line" label="Team Management" />}
                {/* Admin Portal's own content (Framework Management, Framework Mappings) is
                    already backend-enforced as platform-admin-only (see
                    prod-nimbus-admin-frameworks's 403s) - gating the nav link on isTenantAdmin
                    alone stranded platform admins who aren't also a tenant's own admin, unable
                    to reach a page their account can already use once there. */}
                {(isTenantAdmin || isAdmin) && <MenuItem to="/admin" icon="ri-settings-3-line" label="Admin Portal" />}
                {isTenantAdmin && <MenuItem to="/ai-usage" icon="ri-coin-line" label="AI Usage & Spend" />}
                {/* Direct shortcut to what was previously two clicks through Admin Portal - same
                    isAdmin gate as its Field Catalog/Template Builder/Journey Editor siblings
                    below, matching how Framework mutations are actually enforced server-side
                    (platform-admin-only), not the broader isTenantAdmin Admin Portal itself uses. */}
                {isAdmin && <MenuItem to="/admin/frameworks" icon="ri-book-2-line" label="Frameworks" />}
                {isAdmin && <MenuItem to="/admin/field-catalog" icon="ri-database-2-line" label="Field Catalog" />}
                {isAdmin && <MenuItem to="/admin/templates" icon="ri-layout-grid-line" label="Template Builder" />}
                {isAdmin && <MenuItem to="/admin/isms-journey" icon="ri-edit-2-line" label="Journey Editor" />}
                {isSuperAdminUser && <MenuItem to="/super-admin" icon="ri-shield-keyhole-line" label="Super Admin" />}
              </>
            )}

            <MenuItem to="/settings" icon="ri-settings-line" label="Settings" />
          </ul>
        </aside>

        {/* Main Content */}
        <div className="layout-page">
          {/* Navbar */}
          <nav className="layout-navbar container-xxl navbar navbar-expand-xl navbar-detached align-items-center bg-navbar-theme" id="layout-navbar">
            <div className="layout-menu-toggle navbar-nav align-items-xl-center me-3 me-xl-0" onClick={openMenu}>
              <a className="nav-item nav-link px-0 me-xl-4" href="#" onClick={(e) => e.preventDefault()}>
                <i className="ri-menu-fill ri-22px"></i>
              </a>
            </div>

            <div className="navbar-nav-right d-flex align-items-center" id="navbar-collapse">
              <div className="navbar-nav align-items-center">
                <span className="fw-semibold" style={{ fontSize: '1.1rem' }}>
                  Mercell Nimbus: {SPACE_LABELS[space]}
                </span>
              </div>

              <ul className="navbar-nav flex-row align-items-center ms-auto">
                <li className="nav-item me-2 d-none d-sm-block">
                  <a
                    className="nav-link"
                    href="javascript:void(0);"
                    onClick={toggleTheme}
                    title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  >
                    <i className={theme === 'dark' ? 'ri-sun-line ri-22px' : 'ri-moon-line ri-22px'}></i>
                  </a>
                </li>
                <li className="nav-item me-2 d-none d-sm-block">
                  <NavLink to="/settings" className="nav-link">
                    <i className="ri-settings-4-line ri-22px"></i>
                  </NavLink>
                </li>
                <li className="nav-item navbar-dropdown dropdown-user dropdown">
                  <a className="nav-link dropdown-toggle hide-arrow" href="javascript:void(0);" data-bs-toggle="dropdown">
                    <div className="avatar avatar-online">
                      <span className="avatar-initial rounded-circle bg-label-primary">{userInitial}</span>
                    </div>
                  </a>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <div className="dropdown-item">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <span className="fw-medium d-block">{user?.name || 'User'}</span>
                            <small className="text-muted">{user?.email || ''}</small>
                          </div>
                        </div>
                      </div>
                    </li>
                    <li><div className="dropdown-divider"></div></li>
                    <li>
                      <NavLink className="dropdown-item" to="/settings">
                        <i className="ri-settings-4-line ri-22px me-2"></i>Settings
                      </NavLink>
                    </li>
                    <li><div className="dropdown-divider"></div></li>
                    <li>
                      <a className="dropdown-item text-danger" href="javascript:void(0);" onClick={logout}>
                        <i className="ri-logout-box-r-line ri-22px me-2"></i>Logout
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </nav>

          {/* Content */}
          <div className="content-wrapper">
            {/* Keyed on space so switching spaces while already sitting on a shared route (e.g.
                /dashboard) remounts whatever's rendered here fresh, rather than leaving a stale
                render around until the next real navigation — every other route change already
                remounts on its own since the path itself changes. */}
            <Outlet key={space} />

            {/* Footer */}
            <footer className="content-footer footer bg-footer-theme">
              <div className="container-xxl">
                <div className="footer-container d-flex align-items-center justify-content-between py-3 flex-wrap gap-2">
                  <div>© 2026 <a href="#" className="footer-link">Mercell</a></div>
                  {import.meta.env.DEV && (
                  <div className="text-muted">
                    <small><i className="ri-information-line me-1"></i>Development Environment</small>
                  </div>
                  )}
                </div>
              </div>
            </footer>
          </div>
        </div>
      </div>

      {/* Overlay for mobile menu */}
      <div className="layout-overlay" onClick={closeMenu}></div>
    </div>
  );
};

// Main App
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public auth/system routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/callback" element={<AuthCallbackPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
          <Route path="/no-access" element={<NoAccessPage />} />

          {/* Protected app routes */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Risk routes */}
            <Route path="/risks" element={<RiskList />} />
            <Route path="/risks/:riskId" element={<RiskDetail />} />
            <Route path="/risks/new" element={<RiskDetail />} />

            {/* Register-based entity routes */}
            <Route path="/incidents" element={<IncidentList />} />
            <Route path="/incidents/:id" element={<IncidentDetail />} />
            <Route path="/incidents/new" element={<IncidentDetail />} />

            <Route path="/issues" element={<IssueList />} />
            <Route path="/issues/:id" element={<IssueDetail />} />
            <Route path="/issues/new" element={<IssueDetail />} />

            <Route path="/remediations" element={<RemediationList />} />
            <Route path="/remediations/:id" element={<RemediationDetail />} />
            <Route path="/remediations/new" element={<RemediationDetail />} />

            <Route path="/threats" element={<ThreatList />} />
            <Route path="/threats/:id" element={<ThreatDetail />} />
            <Route path="/threats/new" element={<ThreatDetail />} />

            <Route path="/evidence" element={<EvidenceList />} />
            <Route path="/evidence/:id" element={<EvidenceRecordDetail />} />
            <Route path="/evidence/new" element={<EvidenceRecordDetail />} />

            {/* Controls (matrix-based config) */}
            <Route path="/control-matrices" element={<ControlMatricesList />} />
            <Route path="/controls" element={<ControlsList />} />
            <Route path="/control-config" element={<ControlConfigPage />} />
            <Route path="/controls/:id" element={<ControlDetail />} />
            <Route path="/controls/new" element={<ControlDetail />} />

            {/* Governance Inbox */}
            <Route path="/governance-inbox" element={<GovernanceInboxPage />} />

            {/* Documents */}
            <Route path="/documents" element={<DocumentsList />} />
            <Route path="/documents/new" element={<DocumentDetail />} />
            <Route path="/documents/:id" element={<DocumentDetail />} />
            <Route path="/policy-templates" element={<PolicyTemplatesPage />} />

            {/* Hub — HubLayout supplies the persistent content tree; everything else renders
                into its <Outlet/>. :category is validated against HUB_CATEGORIES inside
                HubCategoryOverview, so this one dynamic route covers every category, register-
                backed or not (DPIAs included). */}
            {/* Initiative Tracker/Service Catalog are their own top-level Hub pages, not nested
                under HubLayout — HubLayout always renders the document "Content Library" tree
                alongside its <Outlet/>, which makes sense for the document categories below
                but has nothing to do with initiatives or the service catalog. Both still fall
                under HUB_ONLY_PATH_PREFIXES ('/hub'), so the space-switcher and the app sidebar's
                Hub menu items work exactly the same as any other Hub page. */}
            <Route path="/hub/tracker" element={<HubInitiativeTracker />} />
            <Route path="/hub/service-catalog" element={<HubServiceCatalog />} />
            <Route path="/hub/system-description" element={<HubSystemDescriptionPage />} />
            <Route path="/hub/ai-use-case-wizard" element={<HubAiUseCaseWizard />} />

            <Route path="/hub" element={<HubLayout />}>
              <Route index element={<Navigate to="policies" replace />} />
              <Route path=":category" element={<HubCategoryOverview />} />
              <Route path="documents/:id" element={<HubDocumentPage />} />
            </Route>

            {/* Management Reviews (ISO 27001 §9.3) */}
            <Route path="/management-reviews" element={<ManagementReviewList />} />
            <Route path="/management-reviews/:id" element={<ManagementReviewDetail />} />

            {/* Shared/Admin pages */}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/registers" element={<RegistersPage />} />
            <Route path="/incident-registers" element={<IncidentRegistersPage />} />
            <Route path="/issue-registers" element={<IssueRegistersPage />} />
            <Route path="/remediation-registers" element={<RemediationRegistersPage />} />
            <Route path="/threat-registers" element={<ThreatRegistersPage />} />
            <Route path="/evidence-registers" element={<EvidenceRegistersPage />} />
            <Route path="/document-registers" element={<DocumentRegistersPage />} />
            <Route path="/risk-config" element={<RiskConfigPage />} />
            <Route path="/incident-config" element={<IncidentConfigPage />} />
            <Route path="/issue-config" element={<IssueConfigPage />} />
            <Route path="/remediation-config" element={<RemediationConfigPage />} />
            <Route path="/threat-config" element={<ThreatConfigPage />} />
            <Route path="/evidence-config" element={<EvidenceConfigPage />} />
            <Route path="/departments" element={<DepartmentsPage />} />
            <Route path="/physical-sites" element={<PhysicalSitesPage />} />
            <Route path="/physical-sites/new" element={<PhysicalSiteFormPage />} />
            <Route path="/physical-sites/:id/edit" element={<PhysicalSiteFormPage />} />
            <Route path="/interested-parties" element={<InterestedPartiesPage />} />
            <Route path="/interested-parties/new" element={<InterestedPartyFormPage />} />
            <Route path="/interested-parties/:id/edit" element={<InterestedPartyFormPage />} />
            <Route path="/legal-register" element={<LegalRegisterPage />} />
            <Route path="/legal-register/new" element={<LegalRequirementFormPage />} />
            <Route path="/legal-register/:id/edit" element={<LegalRequirementFormPage />} />
            <Route path="/security-objectives" element={<SecurityObjectivesPage />} />
            <Route path="/security-objectives/new" element={<SecurityObjectiveFormPage />} />
            <Route path="/security-objectives/:id/edit" element={<SecurityObjectiveFormPage />} />
            <Route path="/people" element={<PeoplePage />} />
            <Route path="/assets" element={<AssetsPage />} />
            <Route path="/assets/new" element={<AssetDetailPage />} />
            <Route path="/assets/:id/edit" element={<AssetDetailPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/raci" element={<RaciAdminPage />} />
            <Route path="/raci-bulk" element={<BulkRaciPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admin" element={<AdminPortalPage />} />
            <Route path="/ai-usage" element={<AiUsagePage />} />
            <Route path="/admin/frameworks" element={<AdminFrameworksPage />} />
            <Route path="/frameworks/:id" element={<FrameworkDetail />} />
            <Route path="/admin/mappings" element={<AdminMappingsPage />} />
            <Route path="/admin/field-catalog" element={<FieldCatalogBuilder />} />
            <Route path="/admin/templates" element={<TemplateBuilderPage />} />
            <Route path="/admin/isms-journey" element={<IsmsJourneyAdminPage />} />
            <Route path="/super-admin" element={<SuperAdminPage />} />

            {/* ISO 27001 Readiness Exam */}
            <Route path="/readiness" element={<ReadinessPage />} />
            <Route path="/readiness/:assessmentId" element={<ReadinessWizard />} />
            <Route path="/readiness/:assessmentId/results" element={<ReadinessResults />} />

            {/* ISMS Scope */}
            <Route path="/isms-scope" element={<ISMSScopePage />} />

            {/* Meeting Minutes — dated meetings with typed entries, optionally pinned
                to an initiative. /new is declared before :minutesId; React Router ranks
                static segments above dynamic ones. */}
            <Route path="/meeting-minutes" element={<MeetingMinutesList />} />
            <Route path="/meeting-minutes/new" element={<MeetingMinutesDetail />} />
            <Route path="/meeting-minutes/:minutesId" element={<MeetingMinutesDetail />} />

            {/* Record of Processing Activities — the GDPR Article 30 register, and the
                substrate the DPIA register will reference rather than restate. /new is
                declared before :activityId; React Router ranks static segments above
                dynamic ones, so "new" is never read as an id. */}
            <Route path="/processing-activities" element={<ProcessingActivityList />} />
            <Route path="/processing-activities/new" element={<ProcessingActivityDetail />} />
            <Route path="/processing-activities/report" element={<ProcessingActivityReport />} />
            <Route path="/processing-activities/:activityId" element={<ProcessingActivityDetail />} />
            <Route path="/subprocessors" element={<SubprocessorsList />} />
            <Route path="/subprocessors/new" element={<SubprocessorDetail />} />
            <Route path="/subprocessors/:subprocessorId" element={<SubprocessorDetail />} />
            <Route path="/data-inventory" element={<DataInventoryPage />} />

            {/* AI Governance — AI Use Case Register + dashboard.
                /ai-use-cases/new is declared before the :useCaseId route; React Router
                ranks static segments above dynamic ones, so "new" is never read as an id. */}
            <Route path="/ai-governance" element={<AiDashboard />} />
            <Route path="/ai-use-cases" element={<AiUseCaseList />} />
            <Route path="/ai-use-cases/new" element={<AiUseCaseWizard />} />
            <Route path="/ai-use-cases/report" element={<AiUseCaseReport />} />
            <Route path="/ai-use-cases/:useCaseId" element={<AiUseCaseDetail />} />
            <Route path="/ai-use-cases/:useCaseId/intake/:step" element={<AiUseCaseWizard />} />

            {/* PESTLE Assessment */}
            <Route path="/pestle" element={<PestleListPage />} />
            <Route path="/pestle/:pestleId" element={<PestleWizard />} />

            {/* Statement of Applicability */}
            <Route path="/statement-of-applicability" element={<StatementOfApplicabilityPage />} />

            {/* Reports */}
            <Route path="/reports" element={<ReportsPage />} />

            {/* Maintenance Tasks */}
            <Route path="/maintenance" element={<MaintenanceTasks />} />

            {/* ISMS Journey */}
            <Route path="/isms-journey" element={<IsmsJourneyPage />} />
            <Route path="/isms-journey/:taskId" element={<IsmsTaskGuidePage />} />

            {/* Initiative Tracker (merged from the standalone tracker app) */}
            <Route path="/tracker" element={<TrackerOverviewPage />} />
            <Route path="/tracker/my-actions" element={<MyActionsPage />} />
            <Route path="/tracker/initiatives" element={<AllInitiativesPage />} />
            <Route path="/tracker/initiatives/new" element={<InitiativeFormPage />} />
            <Route path="/tracker/initiatives/:id/edit" element={<InitiativeFormPage />} />
            <Route path="/tracker/initiatives/:id" element={<InitiativeDetailPage />} />
            <Route path="/tracker/initiatives/:id/report" element={<SubmitReportPage />} />
            <Route path="/tracker/initiatives/:id/report/:reportId" element={<SubmitReportPage />} />
            <Route path="/tracker/catalog" element={<TrackerCatalogPage />} />
            <Route path="/tracker/catalog/services/:id" element={<ServiceDetailPage />} />
            <Route path="/tracker/catalog/metrics/:id" element={<MetricDetailPage />} />

            {/* Default — redirect to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Catch-all */}
            <Route
              path="*"
              element={
                <div className="container-xxl flex-grow-1 container-p-y">
                  <div className="misc-wrapper">
                    <h2 className="mb-2 mx-2">Page Not Found</h2>
                    <p className="mb-4 mx-2">The page you're looking for doesn't exist.</p>
                    <a href="/dashboard" className="np-btn np-btn-primary">
                      Back to Dashboard
                    </a>
                  </div>
                </div>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
